<script setup lang="ts">
import {
  instanceOfBeamShapingGroupCircularDto,
  instanceOfBeamShapingGroupRectangularDto,
  instanceOfElectronBeamQualityCombinationValueDto,
  instanceOfPhotonBeamQualityCombinationValueDto,
  ISystemSetupCombinationValue,
  NumericValueDto,
} from '@/api/generator';
import KeyValueTable from '@/components/KeyValueTable/KeyValueTable.vue';
import { translateGroupName } from '@/utils/helper';
import SetupParameterDetails from '@/views/tasks/components/SetupParameterDetails.vue';
import { getSetupParameterName } from '@/views/tasks/utils';
import { Dictionary, groupBy } from 'lodash';
import { computed } from 'vue';

const props = defineProps<{
  title?: string;
  parameters?: ISystemSetupCombinationValue[];
  // Adds a "Name | Value" header row to each table.
  showHeader?: boolean;
  // Puts each group (e.g. "Set 1") in a card; a named group gets its name as the title.
  framedGroups?: boolean;
}>();

function getGroupName(groupName?: string) {
  if (groupName !== 'undefined') {
    return translateGroupName(groupName ?? '');
  }
}

type Row = {
  label: string;
  value: ISystemSetupCombinationValue | NumericValueDto | undefined;
};

function toRow(label: string, value: Row['value']): Row {
  return { label, value };
}

const groups = computed<Dictionary<{ groupName: string; items: Row[] }>>(() =>
  Object.entries(groupBy(props.parameters, 'groupName')).reduce(
    (acc, [groupName, params]) => ({
      ...acc,
      [groupName]: {
        groupName: getGroupName(groupName),
        items: params.reduce<Row[]>((innerAcc, param) => {
          if (instanceOfBeamShapingGroupRectangularDto(param)) {
            // convert BeamShapingGroupRectangularDto to two seperate key value pairs
            innerAcc.push(
              toRow(getSetupParameterName(param.radiationFieldSizeX), param.radiationFieldSizeX),
            );
            innerAcc.push(
              toRow(getSetupParameterName(param.radiationFieldSizeY), param.radiationFieldSizeY),
            );
          } else if (instanceOfBeamShapingGroupCircularDto(param)) {
            // convert BeamShapingGroupCircularDto to a single key value pair
            innerAcc.push(
              toRow(getSetupParameterName(param.radiationFieldSize), param.radiationFieldSize),
            );
          } else if (
            instanceOfPhotonBeamQualityCombinationValueDto(param) ||
            instanceOfElectronBeamQualityCombinationValueDto(param)
          ) {
            innerAcc.push(toRow(getSetupParameterName(param), param));
            innerAcc.push(
              toRow(getSetupParameterName(param.selectedDoseRate), param.selectedDoseRate),
            );
          } else {
            innerAcc.push(toRow(getSetupParameterName(param), param));
          }
          return innerAcc;
        }, []),
      },
    }),
    {},
  ),
);
</script>

<template>
  <template v-if="typeof parameters?.length === 'number' && parameters?.length > 0">
    <h5 v-if="title">{{ title }}</h5>

    <template v-for="group in groups" :key="group.groupName">
      <v-card v-if="framedGroups" elevation="2" class="framed-card mt-3">
        <v-card-text>
          <h6
            v-if="group.groupName"
            class="text-body-2 font-weight-bold text-primary-lighten-1 mb-1"
          >
            {{ group.groupName }}
          </h6>
          <KeyValueTable
            :rows="group.items"
            :labelHeader="showHeader ? $t('Name') : undefined"
            :valueHeader="showHeader ? $t('Value') : undefined"
          >
            <template #label="{ row }">
              <v-label class="text-justify">{{ row.label }}</v-label>
            </template>

            <template #value="{ row }">
              <SetupParameterDetails v-if="row.value" :item="row.value" />
            </template>
          </KeyValueTable>
        </v-card-text>
      </v-card>

      <h6 v-else-if="group.groupName" class="mt-3">{{ $t('Group_one') }}: {{ group.groupName }}</h6>

      <KeyValueTable
        v-if="!framedGroups"
        :rows="group.items"
        :labelHeader="showHeader ? $t('Name') : undefined"
        :valueHeader="showHeader ? $t('Value') : undefined"
      >
        <template #label="{ row }">
          <v-label class="text-justify">{{ row.label }}</v-label>
        </template>

        <template #value="{ row }">
          <SetupParameterDetails v-if="row.value" :item="row.value" />
        </template>
      </KeyValueTable>
    </template>
  </template>
</template>

<style scoped lang="scss">
// The project's card text padding (16px 32px, !important) is too wide for a table.
.framed-card > .v-card-text {
  padding: 16px !important;
}
</style>
